import { useState, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import { Settings } from "lucide-react";
import { getColorByHash } from "@/helper/utils";
import useNotificationStore, {
  type Notification,
} from "@/pages/profile/store/notificationStore";
import useGlobalStore from "@/store/globalStore";
import { useNavigate } from "react-router-dom";

export default function ManageNotifications() {
  const { showError, showSuccess } = useGlobalStore();
  const {
    notifications: storeNotifications,
    isLoading,
    getNotifications,
    markAllAsRead,
  } = useNotificationStore();

  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Fetch notifications on component mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        await getNotifications();
      } catch (error) {
        showError(
          error instanceof Error
            ? error.message
            : "Failed to load notifications"
        );
      }
    };

    fetchNotifications();
  }, []);

  // Update local state when store notifications change
  useEffect(() => {
    if (storeNotifications && Array.isArray(storeNotifications)) {
      setNotifications(storeNotifications);
    }
  }, [storeNotifications]);

  const unreadNotifications = notifications.filter((n) => !n.is_read);
  const readNotifications = notifications.filter((n) => n.is_read);

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      showSuccess("All notifications marked as read!");
    } catch (error) {
      showError(
        error instanceof Error ? error.message : "Failed to mark all as read"
      );
    }
  };

  // Monil Check this Code And Review and let me know if any changes required
  // const getAvatarColor = (groupKey: string) => {
  //   if (groupKey === "media_agency") return "bg-pink-500";
  //   return getColorByHash(groupKey);
  // };
  // My Suggestion
  const renderAvatar = (notification: Notification) => {
    const actor = notification.actor;
    const groupKey = notification.group_key;

    // Actor avatar (best case)
    if (actor?.avatar_url_signed) {
      return (
        <img
          src={actor.avatar_url_signed}
          alt={actor.name}
          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
        />
      );
    }

    // System / group icon
    if (groupKey === "media_agency") {
      return (
        <div className="w-12 h-12 rounded-full bg-pink-500 flex items-center justify-center text-white">
          <Settings className="w-6 h-6" />
        </div>
      );
    }

    // Initials fallback
    const initials = getGroupInitials(groupKey);

    return (
      <div
        className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg flex-shrink-0 ${getColorByHash(
          groupKey
        )}`}
      >
        {initials}
      </div>
    );
  };
  // End Suggestion

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getGroupInitials = (groupKey: string) => {
    const words = groupKey.split("_");
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return groupKey.substring(0, 2).toUpperCase();
  };

  const renderNotificationItem = (notification: Notification) => {
    // const detail = notification.notification;
    // if (!detail) {
    //   // Skip rendering broken / legacy notifications
    //   return null;
    // }
    const groupKey = notification.group_key;
    const initials = getGroupInitials(groupKey);

    return (
      <div key={notification.id} className="py-4">
        <div className="flex items-start gap-3">
          {/* <div
            className={`flex items-center justify-center w-12 h-12 rounded-full text-white font-semibold text-lg flex-shrink-0 ${getAvatarColor(
              groupKey
            )}`}
          >
            {groupKey === "media_agency" ? (
              <Settings className="w-6 h-6" />
            ) : (
              initials
            )}
          </div> */}
          <div className="flex-shrink-0">
            {renderAvatar(notification)}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="text-sm text-gray-500 mb-1">
                  {/* <span className="font-medium">{detail.title}</span> */}
                  <span className="font-medium">{notification.display_title}</span>
                  <span className="mx-2">•</span>
                  <span className="capitalize">
                    {groupKey.replace(/_/g, " ")}
                  </span>
                </div>
                {/* <div className="text-base">{detail.message}</div> */}
                <div className="text-base">{notification.display_message}</div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 flex-shrink-0">
                <span>{formatDate(notification.created_at)}</span>
                {!notification.is_read && (
                  <span className="w-2 h-2 bg-red-500 rounded-full ml-2"></span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">My Notifications</h1>
          <Button
            type="secondary"
            leadingIcon={<Settings className="w-5 h-5" />}
            onClick={() => {
              navigate("/profile");
            }}
          >
            Change Preferences
          </Button>
        </div>

        {isLoading && (
          <div className="text-center text-gray-500 py-20">
            Loading notifications...
          </div>
        )}

        {!isLoading && unreadNotifications.length > 0 && (
          <>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-red-500">New!</h2>
                <span className="bg-red-500 text-white text-sm font-semibold px-2 py-0.5 rounded-full">
                  {unreadNotifications.length}
                </span>
              </div>
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-gray-700 underline hover:text-gray-900"
              >
                Mark all as read
              </button>
            </div>

            <div className="divide-y divide-gray-200 mb-4">
              {unreadNotifications.map((notification) =>
                renderNotificationItem(notification)
              )}
            </div>
          </>
        )}

        {!isLoading && <hr className="border-[#EA33C0]" />}

        {!isLoading && readNotifications.length > 0 && (
          <>
            <h2 className="text-xl font-semibold text-gray-700 mb-6 mt-8">
              Read Notifications
            </h2>
            <div className="divide-y divide-gray-200">
              {readNotifications.map((notification) =>
                renderNotificationItem(notification)
              )}
            </div>
          </>
        )}

        {!isLoading && notifications.length === 0 && (
          <div className="text-center text-gray-500 py-20">
            No notifications found.
          </div>
        )}
      </div>
    </Layout>
  );
}
